'use client'

import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { useDeleteVariants } from '@/lib/api/variants'
import { useToast } from '@/components/ui/use-toast'

interface BulkDeleteVariantsModalProps {
    variantIds: string[]
    onClose: () => void
    onSuccess?: () => void
}

/**
 * Modal component that confirms bulk variant deletion
 */
export const BulkDeleteVariantsModal = ({ variantIds, onClose, onSuccess }: BulkDeleteVariantsModalProps) => {
    const { t } = useTranslation('products')
    const { t: tCommon } = useTranslation('common')
    const { toast } = useToast()
    const deleteVariants = useDeleteVariants()

    const count = variantIds.length

    const handleDelete = async () => {
        if (variantIds.length === 0) return

        try {
            await deleteVariants.mutateAsync({ variantIds })

            toast({
                title: t('bulkDeleteSuccess'),
                description: t('variantsDeleted', { count }),
            })

            onSuccess?.()
            onClose()
        } catch (error) {
            toast({
                title: tCommon('error'),
                description: t('bulkDeleteError'),
                variant: 'destructive',
            })
        }
    }

    return (
        <AlertDialog open={true} onOpenChange={onClose}>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('bulkDeleteVariantsTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>{t('bulkDeleteVariantsConfirmation', { count })}</AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={deleteVariants.isPending}>
                        {tCommon('cancel')}
                    </Button>
                    <Button variant="destructive" onClick={handleDelete} disabled={deleteVariants.isPending}>
                        {deleteVariants.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {tCommon('delete')}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}

